import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import KineticHeadline from "@/components/KineticHeadline";
import HorseHeadLogo from "@/components/HorseHeadLogo";
import hauler from "@/assets/hauler.jpg";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-screen flex-col overflow-hidden"
        >
            <motion.div style={{ y }} className="absolute inset-0">
                <img
                    src={hauler}
                    alt="A rider walking her horse up the ramp of a horse trailer at golden hour"
                    className="h-full w-full scale-[1.12] object-cover object-[40%_center]"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B1612]/80 via-[#0B1612]/35 to-[#0B1612]" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B1612]/85 via-[#0B1612]/35 to-transparent" />

            <header className="relative z-20 flex items-center justify-between px-6 pt-7 md:px-12 md:pt-10">
                <div className="flex items-center gap-3.5" data-testid="brand-lockup">
                    <HorseHeadLogo className="h-11 w-11 md:h-12 md:w-12" />
                    <span className="font-display text-2xl font-semibold tracking-tight text-[#F5F5F0] md:text-[1.7rem]">
                        Mane Miles
                    </span>
                </div>
                <div
                    data-testid="status-chip"
                    className="flex items-center gap-2.5 rounded-full border border-white/15 bg-[#0B1612]/50 px-4 py-2 backdrop-blur-md"
                >
                    <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E0753B] opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#C85A17]" />
                    </span>
                    <span className="font-mono-mm text-[0.6rem] uppercase tracking-[0.28em] text-[#A3B8AD] md:text-xs">
                        Back soon
                    </span>
                </div>
            </header>

            <div className="relative z-20 flex flex-grow items-center px-6 md:px-12">
                <div className="max-w-4xl">
                    <motion.p
                        className="mb-6 flex items-center gap-4 md:mb-8"
                        initial={{ opacity: 0, x: -24 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}
                    >
                        <span className="h-px w-10 bg-[#C85A17] md:w-14" />
                        <span className="font-mono-mm text-[0.65rem] uppercase tracking-[0.32em] text-[#E0753B] md:text-xs">
                            Under construction
                        </span>
                    </motion.p>
                    <KineticHeadline
                        testId="hero-headline"
                        delay={0.55}
                        lines={["Parked for a moment.", "Back on the road soon."]}
                        className="font-display text-[clamp(2.4rem,6.2vw,5.6rem)] font-medium leading-[1.05] tracking-tight text-[#F5F5F0]"
                    />
                    <motion.p
                        className="mt-7 max-w-xl text-base font-light leading-relaxed text-[#A3B8AD] md:mt-9 md:text-lg"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: EASE, delay: 1.15 }}
                    >
                        Mane Miles is getting a fresh start. We&rsquo;ll be back
                        soon to support you and your horse hauling needs.
                    </motion.p>
                </div>
            </div>

            <div className="relative z-20 flex items-end justify-between px-6 pb-8 md:px-12 md:pb-10">
                <div className="flex items-center gap-3">
                    <motion.span
                        className="block h-10 w-px origin-top bg-gradient-to-b from-[#E0753B] to-transparent"
                        animate={{ scaleY: [0.2, 1, 0.2], opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <span className="font-mono-mm text-[0.6rem] uppercase tracking-[0.3em] text-[#A3B8AD]/80">
                        Scroll
                    </span>
                </div>
                <p className="hidden font-mono-mm text-[0.6rem] uppercase tracking-[0.3em] text-[#A3B8AD]/60 sm:block">
                    The load board for horse hauling
                </p>
            </div>
        </section>
    );
}
